import type {
  CatalogedDetectorResponse,
  CatalogedQueryResponse,
  DetectorCatalogResponse,
  QueryParameterResponse,
} from '@prerender/diagnostics-api';
import { useId } from 'react';
import { Alert, Modal } from 'react-bootstrap';

import Loading from '../../app/components/Loading';

import CopyableQuery from './CopyableQuery';
import {
  DETECTOR_QUERIES,
  formatFixedValue,
  parameterKindLabel,
  queryOriginLabel,
  ruleLine,
  unindentedSql,
} from './detectorCatalogLabels';
import { incidentTypeLabel } from './incidentTypeLabels';
import type { Loadable } from './loadable';

type DetectorQueriesDialogProps = {
  open: boolean;
  catalog: Loadable<DetectorCatalogResponse>;
  onClose: () => void;
};

const LOADING_CATALOG_TEXT = 'Loading detector queries';

const CATALOG_FAILED = 'The detector queries could not be loaded.';

function parameterDetail(parameter: QueryParameterResponse): string {
  return parameter.kind === 'fixed' ? formatFixedValue(parameter.value) : parameter.description;
}

function ParameterTable({ parameters }: { parameters: readonly QueryParameterResponse[] }): JSX.Element {
  return (
    <table className="table table-sm diagnostics-catalog-parameters">
      <thead>
        <tr>
          <th scope="col">Parameter</th>
          <th scope="col">Kind</th>
          <th scope="col">Value</th>
        </tr>
      </thead>
      <tbody>
        {parameters.map((parameter) => (
          <tr key={parameter.name}>
            <td>
              <code>{parameter.name}</code>
            </td>
            <td>{parameterKindLabel(parameter.kind)}</td>
            <td>{parameterDetail(parameter)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DetectorQuery({ query }: { query: CatalogedQueryResponse }): JSX.Element {
  const labelId = useId();

  return (
    <article className="diagnostics-catalog-query" aria-labelledby={labelId}>
      <h3 id={labelId} className="diagnostics-catalog-query-label">
        {query.label}
      </h3>
      <p className="diagnostics-catalog-query-origin">{queryOriginLabel(query.purpose, query.source)}</p>
      <CopyableQuery sql={unindentedSql(query.sql)} copyLabel={`Copy the ${query.label} query`} />
      {query.parameters.length > 0 && <ParameterTable parameters={query.parameters} />}
    </article>
  );
}

function DetectorSection({ detector }: { detector: CatalogedDetectorResponse }): JSX.Element {
  const titleId = useId();

  return (
    <section className="diagnostics-detail-section" aria-labelledby={titleId}>
      <h2 id={titleId} className="diagnostics-detail-section-title">
        {incidentTypeLabel(detector.incidentType)}
      </h2>
      <ul className="diagnostics-catalog-rules" aria-label="Rule values">
        {detector.rules.map((rule) => (
          <li key={rule.kind === 'severity' ? rule.severity : rule.name}>{ruleLine(rule)}</li>
        ))}
      </ul>
      {detector.queries.map((query) => (
        <DetectorQuery key={query.label} query={query} />
      ))}
    </section>
  );
}

function CatalogBody({ catalog }: { catalog: Loadable<DetectorCatalogResponse> }): JSX.Element {
  if (catalog.status === 'failed') {
    return <Alert variant="danger">{CATALOG_FAILED}</Alert>;
  }

  if (catalog.status !== 'loaded') {
    return <Loading text={LOADING_CATALOG_TEXT} />;
  }

  return (
    <>
      {catalog.data.detectors.map((detector) => (
        <DetectorSection key={detector.incidentType} detector={detector} />
      ))}
    </>
  );
}

function DetectorQueriesDialog({ open, catalog, onClose }: DetectorQueriesDialogProps): JSX.Element {
  const titleId = useId();

  return (
    <Modal show={open} onHide={onClose} size="xl" scrollable={true} aria-labelledby={titleId}>
      <Modal.Header closeButton={true}>
        <Modal.Title id={titleId}>{DETECTOR_QUERIES}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <CatalogBody catalog={catalog} />
      </Modal.Body>
    </Modal>
  );
}

export default DetectorQueriesDialog;
